import React from 'react';
import { ArrowLeft, RefreshCw, Clock, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';

const RefundPolicy = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <Link to="/" className="btn-secondary" style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px',
              marginBottom: '32px' 
            }}>
              <ArrowLeft size={16} />
              Back to Home
            </Link>
            <h1 className="hero-large" style={{ marginBottom: '24px' }}>
              Refund & Return Policy
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '32px'
            }}>
              We are committed to your satisfaction. Please review our refund and return policy for Care Pharmacy purchases.
            </p>
            <div className="body-small" style={{ color: 'var(--text-light)' }}>
              Last Updated: January 2025
            </div>
          </div>
        </div>
      </section>

      {/* Policy Overview */}
      <section className="section-padding-small" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px',
            marginBottom: '60px'
          }}>
            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <RefreshCw size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                30-Day Return
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Unopened medicines and healthcare products can be returned within 30 days
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <Clock size={40} style={{ color: 'var(--brand-green)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Quick Processing
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Refunds processed within 5-7 business days of return approval
              </p>
            </div>

            <div style={{
              padding: '32px',
              background: 'var(--bg-primary)',
              borderRadius: '0px',
              textAlign: 'center'
            }}>
              <CreditCard size={40} style={{ color: 'var(--brand-blue)', marginBottom: '16px', display: 'inline-block' }} />
              <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                Full Refund
              </h3>
              <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                Complete refund to original payment method for eligible returns
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Policy */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            
            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Return Eligibility
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                marginBottom: '24px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px', color: 'var(--brand-green)' }}>
                  Returnable Items
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Unopened prescription medicines with original packaging</li>
                  <li className="body-regular">• Over-the-counter medications in sealed condition</li>
                  <li className="body-regular">• Healthcare and wellness products</li>
                  <li className="body-regular">• Medical devices and equipment (unused)</li>
                  <li className="body-regular">• Vitamins and supplements (unopened)</li>
                </ul>
              </div>
              
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                border: '1px solid var(--status-error)'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px', color: 'var(--status-error)' }}>
                  Non-Returnable Items
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <li className="body-regular">• Opened or used medicines</li>
                  <li className="body-regular">• Prescription medicines past 7 days from purchase</li>
                  <li className="body-regular">• Personal care items (opened)</li>
                  <li className="body-regular">• Custom-compounded medications</li>
                  <li className="body-regular">• Expired products</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Return Process
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '24px'
              }}>
                {[
                  { step: '1', title: 'Contact Us', description: 'Call +91-6358585323 or visit your nearest Care Pharmacy store' },
                  { step: '2', title: 'Return Authorization', description: 'Provide purchase receipt and reason for return' },
                  { step: '3', title: 'Package Items', description: 'Ensure products are in original packaging and condition' },
                  { step: '4', title: 'Return & Refund', description: 'Bring items to store or arrange pickup for refund processing' }
                ].map((item, index) => (
                  <div key={index} style={{
                    padding: '24px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '0px',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      background: index % 2 === 0 ? 'var(--brand-blue)' : 'var(--brand-green)',
                      color: 'white',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      fontWeight: '500',
                      marginBottom: '16px',
                      margin: '0 auto 16px'
                    }}>
                      {item.step}
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                      {item.title}
                    </h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Refund Timeline & Method
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                  Processing Time
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  Once we receive and inspect your returned item, we will process your refund within 5-7 business days. The refund will be credited to your original payment method.
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <li className="body-regular">• Cash purchases: Immediate cash refund at store</li>
                  <li className="body-regular">• Credit/Debit card: 3-5 business days</li>
                  <li className="body-regular">• Digital payments: 1-3 business days</li>
                  <li className="body-regular">• Bank transfer: 5-7 business days</li>
                </ul>
              </div>
            </div>

            <div style={{ marginBottom: '48px' }}>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Prescription Medicine Returns
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px',
                border: '1px solid var(--brand-blue)'
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  Due to safety regulations and pharmaceutical guidelines, prescription medicines have specific return conditions:
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <li className="body-regular">• Must be returned within 7 days of purchase</li>
                  <li className="body-regular">• Original prescription and packaging required</li>
                  <li className="body-regular">• Unopened and unused condition mandatory</li>
                  <li className="body-regular">• Valid reason for return (wrong medication, allergic reaction, etc.)</li>
                  <li className="body-regular">• Pharmacist verification required before refund</li>
                </ul>
              </div>
            </div>

            <div>
              <h2 className="hero-medium" style={{ marginBottom: '24px' }}>
                Contact Information
              </h2>
              <div style={{
                padding: '32px',
                background: 'var(--bg-secondary)',
                borderRadius: '0px'
              }}>
                <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                  For returns, refunds, or any questions about this policy, please contact us:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div className="body-regular">
                    <strong>Customer Care:</strong> +91-6358585323
                  </div>
                  <div className="body-regular">
                    <strong>Email:</strong> carepharmacyindia@gmail.com
                  </div>
                  <div className="body-regular">
                    <strong>Address:</strong> Care Pharmacy Pvt Ltd, Wall Street-2, Opp. Orient Club, Gujarat College Road, Ahmedabad-380006
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;